import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  LISTING_CONTACT_METHOD_ORDER,
  ListingContactMethod,
  WHATSAPP_DISCLOSURE_TEXT,
  type ListingContactMethodValue,
} from 'constants/listingContactMethod';
import { policyService } from 'api/services/PolicyService';
import { normalizePhoneToInternationalDigits } from 'utils/phoneNumber';

export interface ListingContactOptionsSectionProps {
  contactMethods: ListingContactMethodValue[];
  onContactMethodsChange: (methods: ListingContactMethodValue[]) => void;
  profilePhone: string | null | undefined;
  contactMethodsError: string | null;
  onContactMethodsErrorChange: (message: string | null) => void;
  whatsappAcceptanceChecked: boolean;
  onWhatsappAcceptanceCheckedChange: (checked: boolean) => void;
  whatsappPolicyPending: boolean;
}

const methodLabels: Record<ListingContactMethodValue, string> = {
  [ListingContactMethod.Phone]: 'Telefon',
  [ListingContactMethod.DirectMessage]: 'Direkt Mesaj',
  [ListingContactMethod.Whatsapp]: 'WhatsApp',
};

export function validateListingContactMethods(
  methods: ListingContactMethodValue[],
  profilePhone: string | null | undefined,
  whatsappNeedsAcceptance: boolean,
  whatsappAcceptanceChecked: boolean
): string | null {
  if (methods.length === 0) {
    return 'İlanınızı yayınlamak için en az bir iletişim seçeneği belirlemelisiniz.';
  }

  const needsPhone =
    methods.includes(ListingContactMethod.Phone) ||
    methods.includes(ListingContactMethod.Whatsapp);

  if (needsPhone && !normalizePhoneToInternationalDigits(profilePhone ?? '')) {
    return 'Telefon veya WhatsApp seçmek için profilinize geçerli bir telefon numarası eklemelisiniz.';
  }

  if (
    methods.includes(ListingContactMethod.Whatsapp) &&
    whatsappNeedsAcceptance &&
    !whatsappAcceptanceChecked
  ) {
    return 'WhatsApp iletişimini etkinleştirmek için bildirim metnini kabul etmelisiniz.';
  }

  return null;
}

export function appendContactMethodsToFormData(
  form: FormData,
  methods: ListingContactMethodValue[]
): void {
  methods.forEach((m) => form.append('ContactMethods', m));
  form.set(
    'ShowPhoneOnListing',
    methods.includes(ListingContactMethod.Phone) ? 'true' : 'false'
  );
}

const ListingContactOptionsSection: React.FC<ListingContactOptionsSectionProps> = ({
  contactMethods,
  onContactMethodsChange,
  profilePhone,
  contactMethodsError,
  onContactMethodsErrorChange,
  whatsappAcceptanceChecked,
  onWhatsappAcceptanceCheckedChange,
  whatsappPolicyPending,
}) => {
  const whatsappSelected = contactMethods.includes(ListingContactMethod.Whatsapp);
  const needsPhone =
    contactMethods.includes(ListingContactMethod.Phone) ||
    contactMethods.includes(ListingContactMethod.Whatsapp);
  const hasUsablePhone = Boolean(normalizePhoneToInternationalDigits(profilePhone ?? ''));

  const toggleMethod = (method: ListingContactMethodValue) => {
    const next = contactMethods.includes(method)
      ? contactMethods.filter((m) => m !== method)
      : [...contactMethods, method];
    onContactMethodsChange(next);
    onContactMethodsErrorChange(
      validateListingContactMethods(
        next,
        profilePhone,
        whatsappPolicyPending,
        whatsappAcceptanceChecked
      )
    );
  };

  const orderedSelected = useMemo(
    () => LISTING_CONTACT_METHOD_ORDER.filter((m) => contactMethods.includes(m)),
    [contactMethods]
  );

  return (
    <div className="listing-contact-options">
      <h5 className="card-title mb-3 fw-bold">
        <i className="fas fa-comments me-2 text-primary" aria-hidden="true" />
        İletişim Seçenekleri
      </h5>
      <p className="card-text mb-3">
        Alıcıların bu ilan üzerinden size ulaşabileceği yöntemleri seçin. En az bir seçenek zorunludur.
      </p>

      {LISTING_CONTACT_METHOD_ORDER.map((method) => (
        <div className="form-check mb-2" key={method}>
          <input
            type="checkbox"
            className="form-check-input"
            id={`contactMethod-${method}`}
            checked={contactMethods.includes(method)}
            onChange={() => toggleMethod(method)}
          />
          <label className="form-check-label" htmlFor={`contactMethod-${method}`}>
            {methodLabels[method]}
          </label>
        </div>
      ))}

      {contactMethodsError ? (
        <div className="alert alert-danger py-2 small mb-3" role="alert">
          {contactMethodsError}
        </div>
      ) : null}

      {needsPhone && !hasUsablePhone ? (
        <div className="alert alert-warning py-2 small mb-3" role="alert">
          Telefon veya WhatsApp iletişimi için profilinize geçerli bir telefon numarası eklemelisiniz.{' '}
          <Link to="/profil">Profilim</Link> sayfasından telefon numaranızı güvenlik sekmesinde güncelleyebilirsiniz.
        </div>
      ) : null}

      {whatsappSelected && whatsappPolicyPending ? (
        <div className="border rounded p-3 mb-3 bg-light">
          <p className="small mb-2">{WHATSAPP_DISCLOSURE_TEXT}</p>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="whatsappDisclosureAccept"
              checked={whatsappAcceptanceChecked}
              onChange={(e) => {
                onWhatsappAcceptanceCheckedChange(e.target.checked);
                onContactMethodsErrorChange(
                  validateListingContactMethods(
                    contactMethods,
                    profilePhone,
                    whatsappPolicyPending,
                    e.target.checked
                  )
                );
              }}
            />
            <label className="form-check-label small" htmlFor="whatsappDisclosureAccept">
              WhatsApp bildirim metnini okudum ve kabul ediyorum.
            </label>
          </div>
        </div>
      ) : null}

      {orderedSelected.length > 0 ? (
        <p className="text-muted small mb-0">
          Seçili: {orderedSelected.map((m) => methodLabels[m]).join(', ')}
        </p>
      ) : null}
    </div>
  );
};

export default ListingContactOptionsSection;

export async function acceptWhatsappDisclosureIfNeeded(
  methods: ListingContactMethodValue[],
  whatsappPolicyPending: boolean,
  whatsappAcceptanceChecked: boolean,
  whatsappPolicyId: number | null
): Promise<void> {
  if (
    !methods.includes(ListingContactMethod.Whatsapp) ||
    !whatsappPolicyPending ||
    !whatsappAcceptanceChecked ||
    whatsappPolicyId == null
  ) {
    return;
  }
  await policyService.acceptPolicy(whatsappPolicyId);
}
